import { SIX_TOPIC } from '@/config/networkConstants';
import {
  createFileDeletedMessage,
  createFolderCreatedMessage,
  createFolderDeletedMessage,
} from '@/types/poc/messageFactory';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';

import { publishMessageThunk } from './publishMessageThunk';

export interface FileDeletedEvent {
  type: 'file-deleted';
  sixId: string;
  fileId: string;
}

export interface FolderDeletedEvent {
  type: 'folder-deleted';
  sixId: string;
  id: string;
}

export interface FolderCreatedEvent {
  type: 'folder-created';
  sixId: string;
  folderName: string;
  folderPath: string;
}

export type SixEvent = FileDeletedEvent | FolderDeletedEvent | FolderCreatedEvent;

export function createFileDeletedEvent({
  sixId,
  fileId,
}: Omit<FileDeletedEvent, 'type'>): FileDeletedEvent {
  return {
    type: 'file-deleted',
    sixId,
    fileId,
  };
}

export function createFolderDeletedEvent({
  sixId,
  id,
}: Omit<FolderDeletedEvent, 'type'>): FolderDeletedEvent {
  return {
    type: 'folder-deleted',
    sixId,
    id,
  };
}

export function createFolderCreatedEvent({
  sixId,
  folderName,
  folderPath,
}: Omit<FolderCreatedEvent, 'type'>): FolderCreatedEvent {
  return {
    type: 'folder-created',
    sixId,
    folderName,
    folderPath,
  };
}

function mapEventToPublishArgs(event: SixEvent) {
  switch (event.type) {
    case 'file-deleted':
      return {
        sixId: event.sixId,
        topic: SIX_TOPIC(event.sixId),
        message: createFileDeletedMessage({
          sixId: event.sixId,
          fileId: event.fileId,
        }),
      };
    case 'folder-deleted':
      return {
        sixId: event.sixId,
        topic: SIX_TOPIC(event.sixId),
        message: createFolderDeletedMessage({
          sixId: event.sixId,
          folderId: event.id,
        }),
      };
    case 'folder-created':
      return {
        sixId: event.sixId,
        topic: SIX_TOPIC(event.sixId),
        message: createFolderCreatedMessage({
          sixId: event.sixId,
          folderName: event.folderName,
          folderPath: event.folderPath,
        }),
      };
  }
}

export const publishSixEventThunk = createAppAsyncThunk<
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- matches the existing thunk pattern for actions with no fulfilled payload
  void,
  SixEvent
>(
  'messaging/publishSixEvent',
  async(event, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(publishMessageThunk(mapEventToPublishArgs(event))).unwrap();
    } catch (error) {
      const message =
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
          ? error.message
          : String(error);

      return rejectWithValue({ message });
    }
  }
);

export const publishSixEvent = {
  fileDeleted(args: Omit<FileDeletedEvent, 'type'>) {
    return publishSixEventThunk(createFileDeletedEvent(args));
  },
  folderDeleted(args: Omit<FolderDeletedEvent, 'type'>) {
    return publishSixEventThunk(createFolderDeletedEvent(args));
  },
  folderCreated(args: Omit<FolderCreatedEvent, 'type'>) {
    return publishSixEventThunk(createFolderCreatedEvent(args));
  },
};
